import { convertUrl, debugState } from '../index';
import { randomInt, v2 } from '../helpers';
import {
  ImageId,
  PlaygroundFont,
  SpriteSheet,
  type ImageState,
  type ImageTable,
  type PlaygroundImage,
} from '../types';
import logger from '../controllers/logger';

let worker: Worker = null;
const spriteSheets: { [index: ImageId]: SpriteSheet } = {};
const fonts: PlaygroundFont[] = [];
const imageTable: ImageTable = {};

let fontsSentToWorker = 0;
let fontsLoadedByWorker = 0;

let errorImg = null;

const getZeroPlaygroundImage: PlaygroundImage = {
  state: '',
  img: null,
  uri: '',
  onLoad: null,
  width: 0,
  height: 0,
  aspect: 0,
};

export function init(_worker: Worker) {
  worker = _worker;

  const imageId = '_error';
  const uri = 'images/image_not_ready.png';
  const ErrorPlaygroundImage: PlaygroundImage = {
    ...getZeroPlaygroundImage,
    state: 'loading',
    uri,
    img: createImage(imageId, uri),
  };

  imageTable[imageId] = ErrorPlaygroundImage;
  errorImg = ErrorPlaygroundImage;
}

export function loadImageListInDataController(imageList: string[][]) {
  const length = imageList.length;

  for (let i = 0; i < length; i++) {
    const imageLoadData = imageList[i];
    const imageId = imageLoadData[0];
    const uri = imageLoadData[1];

    cacheImage(uri, imageId);
  }
}

export async function cacheImage(imageUri: string, imageId: string, forceReload = false) {
  const existing = imageTable[imageId];
  if (existing && existing.uri === imageUri) {
    if (!forceReload) {
      return;
    }
  }

  const newPlaygroundImage: PlaygroundImage = {
    ...getZeroPlaygroundImage,
    state: 'loading',
    uri: imageUri,
    img: null,
  };

  imageTable[imageId] = newPlaygroundImage;

  if (worker) {
    let response: Response = null;

    try {
      response = await fetch(imageUri);
    } catch (e) {
      newPlaygroundImage.state = 'error';
      logger.warn(e);
      return;
    }

    let imageBlob: Blob = null;

    try {
      imageBlob = await response.blob();
    } catch (e) {
      newPlaygroundImage.state = 'error';
      logger.warn(e);
      return;
    }

    const isSVG = imageUri.indexOf('.svg') > 0;

    let imageBitmap: ImageBitmap = null;
    let width = 0;
    let height = 0;

    try {
      if (isSVG) {
        width = 256;
        height = 256;
        imageBitmap = await svgToBitmap(imageBlob, width, height);
      } else {
        imageBitmap = await createImageBitmap(imageBlob);
        width = imageBitmap.width;
        height = imageBitmap.height;
      }
    } catch (e) {
      newPlaygroundImage.state = 'error';
      logger.error('CacheImage error: ', { imageId, error: e });
      return;
    }

    // 3. Send the bitmap to the worker. The second argument is the
    //    transfer list, which transfers ownership with zero-copy.
    worker.postMessage(
      {
        type: 'load-image',
        id: imageId,
        bitmap: imageBitmap,
      },
      [imageBitmap],
    );

    newPlaygroundImage.width = width;
    newPlaygroundImage.height = height;
    if (height) {
      newPlaygroundImage.aspect = width / height;
    }

    newPlaygroundImage.state = 'success';

    const spriteSheetsThatNeedThisImage = Object.values(spriteSheets).filter(
      (it) => !it.complete && it.imageId === imageId,
    );

    const length = spriteSheetsThatNeedThisImage.length;

    if (length > 0) {
      for (let i = 0; i < spriteSheetsThatNeedThisImage.length; i++) {
        setSpriteSheetData(spriteSheetsThatNeedThisImage[i], newPlaygroundImage);
      }
    }
  } else {
    newPlaygroundImage.img = createImage(imageId, imageUri);
  }
}

export async function loadImageBitmapInDataController(
  // dataController: DataController,
  imageBitmap: ImageBitmap,
  imageId: string,
  forceReload = false,
) {
  const existing = imageTable[imageId];
  if (existing) {
    if (!forceReload) {
      return;
    }
  }

  const newPlaygroundImage: PlaygroundImage = {
    ...getZeroPlaygroundImage,
    state: 'loading',
    uri: '',
    img: null,
  };

  imageTable[imageId] = newPlaygroundImage;

  // newPlaygroundImage.img = loadImage(dataController, imageId, imageUri);

  if (worker) {
    let width = 0;
    let height = 0;

    try {
      width = imageBitmap.width;
      height = imageBitmap.height;
    } catch (e) {
      newPlaygroundImage.state = 'error';
      logger.error(e);
      return;
    }

    // 3. Send the bitmap to the worker. The second argument is the
    //    transfer list, which transfers ownership with zero-copy.
    worker.postMessage(
      {
        type: 'load-image',
        id: imageId,
        bitmap: imageBitmap,
      },
      [imageBitmap],
    );

    newPlaygroundImage.width = width;
    newPlaygroundImage.height = height;
    if (height) {
      newPlaygroundImage.aspect = width / height;
    }

    newPlaygroundImage.state = 'success';

    const spriteSheetsThatNeedThisImage = Object.values(spriteSheets).filter(
      (it) => !it.complete && it.imageId === imageId,
    );

    const length = spriteSheetsThatNeedThisImage.length;

    if (length > 0) {
      for (let i = 0; i < spriteSheetsThatNeedThisImage.length; i++) {
        setSpriteSheetData(spriteSheetsThatNeedThisImage[i], newPlaygroundImage);
      }
    }
  } else {
    newPlaygroundImage.img = createImage(imageId, '');
  }
}

async function svgToBitmap(svgData, width, height): Promise<ImageBitmap> {
  // 1. Create an Image object
  const img = new Image();

  // 2. Create a data URL from the SVG string
  // If svgData is already a URL, you can skip this step and just set img.src = svgData
  const svgBlob = new Blob([svgData], {
    type: 'image/svg+xml;charset=utf-is',
  });
  const url = URL.createObjectURL(svgBlob);

  // 3. Load the SVG into the Image object
  img.src = url;

  return new Promise((resolve, reject) => {
    img.onload = async () => {
      // 4. Clean up the object URL
      URL.revokeObjectURL(url);

      try {
        // 5. Use createImageBitmap to get a high-performance, transferable bitmap
        // This is often more efficient than using a 2D canvas directly.
        const imageBitmap = await createImageBitmap(img, {
          resizeWidth: width,
          resizeHeight: height,
        });
        resolve(imageBitmap);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(`Failed to load SVG: ${e}`);
    };
  });
}

export function cacheImageFromBlob(blob: Blob, imageId: string) {
  const uri = URL.createObjectURL(blob);
  cacheImage(uri, imageId);
}

export function cacheImageFromBase64(dataBase64: string, imageId: string) {
  if (imageTable[imageId] !== undefined) {
    logger.debug('This imageId is already in use, cannot re-use.', { imageId });
  } else {
    const newPlaygroundImage: PlaygroundImage = {
      ...getZeroPlaygroundImage,
      state: 'loading',
      img: createImage(imageId, dataBase64),
    };

    imageTable[imageId] = newPlaygroundImage;
  }
}

export async function cacheImageFromApiAsync(
  token: string,
  imageUri: string,
  imageId: ImageId,
  onLoad: (HTMLElement) => void = null,
  forceReload = false,
  forceMainThread = false,
) {
  const existing = imageTable[imageId];

  if (existing && existing.uri === imageUri) {
    if (existing.state === 'loading' || existing.state === 'success') {
      if (!forceReload) {
        return;
      }
    }
  }

  const newPlaygroundImage: PlaygroundImage = {
    ...getZeroPlaygroundImage,
    state: 'loading',
    uri: imageUri,
    onLoad,
  };

  imageTable[imageId] = newPlaygroundImage;

  const headers = { Authorization: 'Bearer ' + token };

  const requestOptions = {
    method: 'GET',
    headers,
  };

  let req = null;
  const convertedUri = convertUrl(imageUri);

  try {
    req = await fetch(convertedUri, requestOptions);
  } catch {
    console.warn('Cannot fetch ' + convertedUri);
    newPlaygroundImage.state = 'error';
    return;
  }

  if (req?.ok) {
    const blob = await req.blob();

    if (worker && !forceMainThread) {
      let imageBitmap: ImageBitmap = null;
      try {
        imageBitmap = await createImageBitmap(blob);
      } catch (e) {
        logger.error('Cannot create image bitmap', e);
        return;
      }

      const w = imageBitmap.width;
      const h = imageBitmap.height;

      newPlaygroundImage.width = w;
      newPlaygroundImage.height = h;
      if (h) {
        newPlaygroundImage.aspect = w / h;
      }

      newPlaygroundImage.state = 'success';

      worker.postMessage(
        {
          type: 'load-image',
          id: imageId,
          bitmap: imageBitmap,
        },
        [imageBitmap],
      );
    } else {
      const url = URL.createObjectURL(blob);

      const img = createImage(imageId, url);
      newPlaygroundImage.img = img;
    }
  } else {
    newPlaygroundImage.state = 'error';
  }
}

export function unloadImages(imageIds: ImageId[]) {
  for (let i = imageIds.length - 1; i >= 0; i--) {
    unloadImage(imageIds[i]);
  }
}

export function unloadImage(imageId: ImageId) {
  const [success, img] = getImage(imageId);

  if (worker) {
    worker.postMessage({
      type: 'unload-image',
      id: imageId,
    });
  } else {
    if (success) {
      URL.revokeObjectURL(img.src);
    } else {
      console.warn('Cannot unload Image, imageId not found', { imageId });
    }
  }

  if (imageTable[imageId]) {
    delete imageTable[imageId];
  }
}

export function createImage(
  // dataController: DataController,
  imageId: ImageId,
  src: string,
): HTMLImageElement {
  let newImage = new Image();

  const imageLoaded = () => {
    onImageLoaded(imageId, newImage);
  };
  const imageError = () => {
    onImageLoadError(imageId, src);
  };

  newImage.src = src;
  if (newImage.complete) {
    onImageLoaded(imageId, newImage);
  } else {
    newImage.onload = imageLoaded;
    newImage.onerror = imageError;
  }

  return newImage;
}

function onImageLoaded(imageId: ImageId, imgElement: HTMLImageElement) {
  const pgImage = imageTable[imageId];

  if (pgImage) {
    if (imgElement) {
      pgImage.state = 'success';

      const w = imgElement.naturalWidth;
      const h = imgElement.naturalHeight;
      pgImage.width = w;
      pgImage.height = h;
      if (h) {
        pgImage.aspect = w / h;
      }

      const spriteSheetsThatNeedThisImage = Object.values(spriteSheets).filter(
        (it) => !it.complete && it.imageId === imageId,
      );

      const length = spriteSheetsThatNeedThisImage.length;

      if (length > 0) {
        for (let i = 0; i < spriteSheetsThatNeedThisImage.length; i++) {
          setSpriteSheetData(spriteSheetsThatNeedThisImage[i], pgImage);
        }
      }

      if (pgImage.onLoad) {
        pgImage.onLoad(imgElement);
      }
    } else {
      pgImage.state = 'error';
      logger.error('Loaded, but no img object set', imageId);
    }
  }
}

function onImageLoadError(imageId: ImageId, src: string) {
  if (src) {
    console.warn('Cannot load image: ', src);
  }

  const im = imageTable[imageId] as PlaygroundImage | undefined;

  if (im) {
    im.state = 'error';
  }
}

export function isImageCached(imageId: string): boolean {
  return imageTable[imageId] !== undefined;
}

export function getImageState(imageId: string): ImageState {
  const playgroundImage = imageTable[imageId] as PlaygroundImage | undefined;
  const success = playgroundImage && playgroundImage.state === 'success';

  return playgroundImage ? playgroundImage.state : '';
}

export function getImage(imageId: string, forceMainthread = false): [boolean, HTMLImageElement] {
  const playgroundImage = imageTable[imageId] as PlaygroundImage | undefined;
  const success = playgroundImage && playgroundImage.state === 'success';

  if (worker && !forceMainthread) {
    return [success, null];
  } else if (success) {
    if (!playgroundImage.img) {
      return [false, null];
    }

    return [success, playgroundImage.img];
  } else if (playgroundImage && playgroundImage.state === 'error' && imageId !== 'broken') {
    return [false, null];
  } else if (playgroundImage && playgroundImage.img) {
    return [false, playgroundImage.img];
  } else {
    return [false, null];
  }
}

export function getSpriteSheet(id: string): SpriteSheet | null {
  if (spriteSheets[id]) {
    return spriteSheets[id];
  } else {
    return null;
  }
}

export function getImageAspectRatio(imageId: string): number {
  const playgroundImage = imageTable[imageId] as PlaygroundImage | undefined;

  return playgroundImage ? playgroundImage.aspect : 0;
}

export function addSpriteSheet(
  spriteSheetId: string,
  imageId: ImageId,
  columns: number,
  rows: number,
): SpriteSheet {
  let spriteSheet: SpriteSheet = {
    imageId,
    image: null,
    rows,
    columns,
    spriteWidth: 0,
    spriteHeight: 0,
    positionTable: [],
    complete: false,
    spriteCount: rows * columns,
  };

  spriteSheets[spriteSheetId] = spriteSheet;

  const pgImage = imageTable[imageId];
  if (!pgImage) {
    logger.debug('Please load image before adding a spritesheet with that image.');
    return;
  }

  if (pgImage.state === 'success') {
    setSpriteSheetData(spriteSheet, pgImage);
  }

  return spriteSheet;
}

export function setSpriteSheetData(spriteSheet: SpriteSheet, pgImage: PlaygroundImage) {
  spriteSheet.image = pgImage.img;

  spriteSheet.spriteWidth = pgImage.width / spriteSheet.columns;
  spriteSheet.spriteHeight = pgImage.height / spriteSheet.rows;

  for (let r = 0; r < spriteSheet.rows; r++) {
    for (let c = 0; c < spriteSheet.columns; c++) {
      const x = c * spriteSheet.spriteWidth;
      const y = r * spriteSheet.spriteHeight;
      spriteSheet.positionTable.push(v2(x, y));
    }
  }

  spriteSheet.complete = true;
}

export function getSpriteSheetIndexOrZero(spriteSheet: SpriteSheet, maybeIndex: number): number {
  if (maybeIndex >= 0 && maybeIndex < spriteSheet.spriteCount) {
    return maybeIndex;
  } else {
    return 0;
  }
}

export function getRandomSpriteIndex(spriteSheet: SpriteSheet): number {
  return randomInt(0, spriteSheet.spriteCount);
}

export function loadFonts(_fonts: { fontId: string; fontUri: string }[], callback: Function) {
  for (let i = 0; i < _fonts.length; i++) {
    const fontId = _fonts[i].fontId;
    const fontUri = _fonts[i].fontUri;

    const playgroundFont: PlaygroundFont = {
      id: fontId,
      fontFace: new FontFace(fontId, `url(${fontUri})`),
      baseLineOffsetPerSize: 0.2,
      loaded: false,
    };

    fonts.push(playgroundFont);

    playgroundFont.fontFace.load().then((font) => {
      playgroundFont.loaded = true;
      document.fonts.add(font);
      const loadedFontsCount = fonts.filter((it) => it.loaded).length;
      if (loadedFontsCount === fonts.length) {
        callback();
      }
    });
  }
}

export async function loadAndSendFontToWorker(worker, fontId, fontUri) {
  try {
    // 1. Fetch the font file as a raw data buffer
    const response = await fetch(fontUri);
    const fontBuffer = await response.arrayBuffer();

    // 2. Send a message with the font data.
    // The ArrayBuffer is included in the transfer list for a zero-copy transfer.

    worker.postMessage(
      {
        type: 'load-font',
        fontId: fontId,
        fontBuffer: fontBuffer,
      },
      [fontBuffer],
    );
  } catch (e) {
    console.error(`Failed to load font: ${fontUri}`, e);
  }
}

